/**
 * PublicPassportPage — Página pública read-only do passaporte de um piloto.
 * URL: /passport/:userId
 * Consome GET /api/gamification/passport/public/:userId (sem autenticação).
 * Layout rugged idêntico ao AutocolantesPage mas sem interacção.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STICKERS = [
  { key: 'novato',      icon: '🚙', text: 'NOVATO 4×4',     sub: null,        color: '#F2EBDD', bg: '#3D362E', minXp: 0 },
  { key: 'trailblazer', icon: '🛞', text: 'TRAILBLAZER',    sub: 'PT · 4×4',  color: '#0F0D0B', bg: '#E87A2A', minXp: 500 },
  { key: 'summit',      icon: '🏔️', text: 'SUMMIT',        sub: null,        color: '#F2EBDD', bg: '#2B2A25', minXp: 1500 },
  { key: 'nightrider',  icon: '🌙', text: 'NIGHT RIDER',    sub: '☾ ★',       color: '#0F0D0B', bg: '#C4A275', minXp: 3000 },
  { key: 'expedition',  icon: '⛰️', text: 'EXPEDITION 4×4', sub: null,        color: '#F2EBDD', bg: '#4E5D3F', minXp: 5000 },
  { key: 'terrabruta',  icon: '🪨', text: 'TERRA BRUTA',    sub: 'PT · 4×4',  color: '#F2EBDD', bg: '#2B2A25', minXp: 8000 },
  { key: 'crest',       icon: '⚔️', text: 'BRASÃO',         sub: null,        color: '#FACC15', bg: '#1A1613', minXp: 12000 },
  { key: 'finisher',    icon: '🏁', text: 'FINISHER',       sub: '★★★',       color: '#0F0D0B', bg: '#FACC15', minXp: 20000 },
];

const STAMP_ICONS = ['⛰️','🌲','🏖️','🌊','🏔️','⚡','💦','🧭','🌙','☀️','🏁','🛞','🏆','🔥','👑','★','✧','✦'];

export default function PublicPassportPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/gamification/passport/public/${encodeURIComponent(userId)}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((err) => {
        setError(err?.response?.status === 404 ? 'Piloto não encontrado' : 'Erro a carregar passaporte');
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const xp = data?.stats?.xp_total || 0;
  const km = Math.round(data?.stats?.km_total || 0);
  const routes = data?.stats?.routes_completed || 0;
  const streak = data?.stats?.streak_days || 0;
  const stampsCount = data?.stamps_count || 0;
  const owned = useMemo(() => new Set(data?.vehicle?.decals || []), [data]);
  const nextSticker = STICKERS.filter((s) => !owned.has(s.key) && s.minXp > xp).sort((a, b) => a.minXp - b.minXp)[0];
  const tier = xp >= 20000 ? { name: 'PRATA', color: '#C0C0C0' }
             : xp >= 8000  ? { name: 'BRONZE II', color: '#B87333' }
             : xp >= 2000  ? { name: 'BRONZE I', color: '#8B5A2B' }
             : { name: 'INICIANTE', color: '#6B5D4B' };
  const displayName = data?.user?.name || 'Piloto Tectur';
  const initials = displayName.slice(0, 2).toUpperCase();
  const role = data?.user?.role;
  const monoStyle = { fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div
      className="min-h-screen"
      style={{
        background: '#0F0D0B',
        backgroundImage: 'url(/topo_bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundBlendMode: 'overlay',
      }}
      data-testid="public-passport-page"
    >
      <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg, rgba(15,13,11,.75), rgba(15,13,11,.92))', pointerEvents: 'none' }} />

      {/* Top bar */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b"
        style={{ background: 'rgba(15,13,11,.95)', borderColor: '#4A443A', backdropFilter: 'blur(8px)' }}
      >
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[#F2EBDD] hover:text-[#E87A2A] transition"
          style={{ ...monoStyle, fontSize: 11, letterSpacing: 1.5, fontWeight: 900, textTransform: 'uppercase' }}
          data-testid="public-passport-back"
        >
          ← Dashboard
        </button>
        <div className="flex items-center gap-2">
          <span className="text-2xl">🏆</span>
          <div>
            <h1 style={{ ...monoStyle, fontSize: 16, letterSpacing: 3, fontWeight: 900, color: '#F2EBDD' }}>PASSAPORTE</h1>
            <p style={{ ...monoStyle, fontSize: 9, color: '#C4A275', letterSpacing: 1.5, marginTop: 2 }}>
              READ-ONLY · TECTUR 4×4 CLUB
            </p>
          </div>
        </div>
        <div style={{ width: 100 }} />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="text-center py-20 text-[#C4A275]" style={monoStyle} data-testid="public-passport-loading">A carregar…</div>
        ) : error ? (
          <div
            className="text-center py-20 rounded-lg border"
            style={{ borderColor: '#4A443A', background: 'rgba(26,22,19,.85)', color: '#EF4444' }}
            data-testid="public-passport-error"
          >
            <div style={{ ...monoStyle, fontSize: 32, marginBottom: 12 }}>🚫</div>
            <div style={{ ...monoStyle, fontSize: 14, fontWeight: 900, letterSpacing: 2 }}>{error}</div>
          </div>
        ) : (
          <>
            {/* PASSAPORTE */}
            <section
              className="rounded-xl border p-5"
              style={{ background: 'rgba(26,22,19,.85)', borderColor: '#4A443A', backdropFilter: 'blur(6px)' }}
              data-testid="public-passport-card"
            >
              <div className="flex items-center gap-4">
                <div style={{
                  width: 68, height: 68, borderRadius: '50%', border: '4px solid #E87A2A',
                  background: 'linear-gradient(135deg,#C4A275,#E87A2A)',
                  display: 'grid', placeItems: 'center',
                  ...monoStyle, fontSize: 20, fontWeight: 900, color: '#0F0D0B',
                }}>
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ ...monoStyle, fontSize: 18, fontWeight: 900, color: '#F2EBDD', letterSpacing: 1 }}>
                    {displayName}
                  </div>
                  <div style={{ ...monoStyle, fontSize: 10, color: '#C4A275', letterSpacing: 1.5, marginTop: 3 }}>
                    ◈ 4×4 CLUB · MEMBRO{role === 'admin' ? ' · ADMIN' : role === 'staff' ? ' · STAFF' : ''}
                  </div>
                </div>
                <div className="px-3 py-1.5 rounded" style={{ background: tier.color, color: '#0F0D0B', ...monoStyle, fontSize: 11, fontWeight: 900, letterSpacing: 2 }}>
                  {tier.name}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                <StatCell value={xp.toLocaleString('pt-PT')} label="XP TOTAL" />
                <StatCell value={km.toLocaleString('pt-PT')} label="KM" />
                <StatCell value={routes} label="ROTAS" />
                <StatCell value={`${streak}d`} label="STREAK" />
              </div>

              {nextSticker && (
                <div className="mt-4">
                  <div className="flex justify-between mb-2" style={{ ...monoStyle, fontSize: 10, color: '#C4A275' }}>
                    <span>PRÓXIMO · <b style={{ color: '#F2EBDD' }}>{nextSticker.text}</b></span>
                    <span>{(nextSticker.minXp - xp).toLocaleString('pt-PT')} XP</span>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden" style={{ background: '#0F0D0B', border: '1px solid rgba(74,68,58,.6)' }}>
                    <div style={{
                      width: `${Math.min(100, (xp / (nextSticker?.minXp || 1)) * 100)}%`,
                      background: 'linear-gradient(90deg, #E87A2A, #FACC15)',
                      transition: 'width .6s ease', height: '100%',
                    }} />
                  </div>
                </div>
              )}
            </section>

            {/* AUTOCOLANTES read-only */}
            <section>
              <SectionHeader title="▸ COLEÇÃO DE AUTOCOLANTES" count={`${owned.size} / ${STICKERS.length}`} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                {STICKERS.map((s) => {
                  const isOwned = owned.has(s.key);
                  const locked = !isOwned && s.minXp > xp;
                  return (
                    <div
                      key={s.key}
                      className="aspect-square rounded-lg p-3 flex flex-col items-center justify-center relative overflow-hidden border-2"
                      style={{
                        borderStyle: locked ? 'dashed' : 'solid',
                        borderColor: locked ? 'rgba(196,162,117,.25)' : (isOwned ? 'rgba(0,0,0,.4)' : 'rgba(196,162,117,.20)'),
                        background: isOwned ? s.bg : (locked ? 'rgba(26,22,19,.40)' : 'rgba(26,22,19,.85)'),
                        opacity: locked ? 0.55 : 1,
                        boxShadow: isOwned ? '0 6px 18px rgba(0,0,0,.35)' : 'none',
                      }}
                    >
                      {locked && <span style={{ position: 'absolute', top: 6, right: 8, fontSize: 12, opacity: 0.75 }}>🔒</span>}
                      {isOwned && <span style={{ position: 'absolute', top: 6, right: 8, fontSize: 12, color: s.color }}>✓</span>}
                      <span style={{ fontSize: 36, lineHeight: 1, marginBottom: 6 }}>{s.icon}</span>
                      <div style={{ ...monoStyle, fontWeight: 900, fontSize: 10, color: isOwned ? s.color : '#F2EBDD', letterSpacing: 1, textAlign: 'center' }}>
                        {s.text}
                      </div>
                      {s.sub && (
                        <div style={{ ...monoStyle, fontWeight: 700, fontSize: 8, color: isOwned ? s.color : '#F2EBDD', letterSpacing: 1, opacity: 0.85, marginTop: 2 }}>
                          {s.sub}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Carimbos read-only */}
            <section>
              <SectionHeader title="▸ CARIMBOS DO PASSAPORTE" count={`${stampsCount} / ${STAMP_ICONS.length}`} />
              <div className="grid grid-cols-6 md:grid-cols-9 gap-3 mt-3">
                {STAMP_ICONS.map((icon, i) => {
                  const unlocked = i < stampsCount;
                  return (
                    <div key={i} className="aspect-square rounded-full border-2 flex items-center justify-center relative" style={{
                      borderStyle: unlocked ? 'solid' : 'dashed',
                      borderColor: unlocked ? '#E87A2A' : 'rgba(196,162,117,.25)',
                      background: unlocked ? 'rgba(232,122,42,.10)' : 'rgba(26,22,19,.85)',
                      opacity: unlocked ? 1 : 0.4,
                      filter: unlocked ? 'none' : 'grayscale(1)',
                    }}>
                      <span style={{ fontSize: 22 }}>{icon}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Footer CTA */}
            <div className="text-center pt-2 pb-6" style={monoStyle}>
              <a
                href="/login"
                className="inline-block px-4 py-2 rounded"
                style={{ background: '#E87A2A', color: '#0F0D0B', fontWeight: 900, letterSpacing: 2, fontSize: 11, textTransform: 'uppercase', textDecoration: 'none' }}
                data-testid="public-passport-join"
              >
                Junta-te ao clube →
              </a>
              <div style={{ fontSize: 10, color: '#C4A275', marginTop: 10 }}>TECTUR 4×4 · offline-first navigation</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCell({ value, label }) {
  const monoStyle = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div className="rounded-lg p-3 text-center border" style={{ background: 'rgba(15,13,11,.60)', borderColor: 'rgba(196,162,117,.15)' }}>
      <div style={{ ...monoStyle, fontWeight: 900, fontSize: 20, color: '#E87A2A', lineHeight: 1 }}>{value}</div>
      <div style={{ ...monoStyle, fontWeight: 900, fontSize: 9, color: '#C4A275', letterSpacing: 1.2, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function SectionHeader({ title, count }) {
  const monoStyle = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div className="flex items-baseline justify-between">
      <h3 style={{ ...monoStyle, fontWeight: 900, fontSize: 13, color: '#E87A2A', letterSpacing: 3 }}>{title}</h3>
      <span style={{ ...monoStyle, fontSize: 11, color: 'rgba(242,235,221,.55)' }}>{count}</span>
    </div>
  );
}
